// @ts-nocheck
import { Link, useLocation } from "wouter";
import { cn } from "../../lib/utils";
import { useLang } from "../../lib/lang";
import { useAiBot } from "../../lib/ai-bot-context";

interface NavItem {
  href: string;
  icon: string;
}

const mainNavItems: NavItem[] = [
  { href: "/dashboard", icon: "fas fa-th-large" },
  { href: "/devices", icon: "fas fa-mobile-alt" },
  { href: "/templates", icon: "fas fa-file-alt" },
  { href: "/single-send", icon: "fas fa-paper-plane" },
  { href: "/chatbot", icon: "fas fa-robot" },
  { href: "/ai-bot", icon: "fas fa-brain" },
  { href: "/my-apps", icon: "fas fa-cube" },
  { href: "/contacts", icon: "fas fa-address-book" },
  { href: "/campaigns", icon: "fas fa-rocket" },
  { href: "/scheduled", icon: "fas fa-calendar-alt" },
  { href: "/messages", icon: "fas fa-list-ul" },
  { href: "/webhooks", icon: "fas fa-project-diagram" },
];

const settingsNavItems: NavItem[] = [
  { href: "/subscription", icon: "fas fa-rocket" },
  { href: "/support", icon: "fas fa-headset" },
  { href: "/profile", icon: "fas fa-cog" },
  { href: "/auth-key", icon: "fas fa-key" },
];

function NavLink({ item }: { item: NavItem }) {
  const [location] = useLocation();
  const { t } = useLang();
  const isActive = location === item.href || location.startsWith(item.href);
  const label = t.nav[item.href as keyof typeof t.nav] ?? item.href;

  return (
    <li className="nav-item mb-0.5">
      <Link
        href={item.href}
        className={cn(
          "flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-all duration-150",
          isActive
            ? "bg-[#eef0fd] text-[#1171ef] font-semibold"
            : "text-[#525f7f] hover:text-[#1171ef] hover:bg-[#f5f7ff] font-medium"
        )}
      >
        <i className={cn(item.icon, "w-5 text-center text-base shrink-0", isActive ? "text-[#1171ef]" : "text-[#8898aa]")} />
        <span className="flex-1 text-[13px]">{label}</span>
      </Link>
    </li>
  );
}

function AiBotNavLink() {
  const [location] = useLocation();
  const { t } = useLang();
  const { enabled, toggle } = useAiBot();
  const isActive = location === "/ai-bot" || location.startsWith("/ai-bot");
  const label = t.nav["/ai-bot" as keyof typeof t.nav] ?? "AI Bot";

  return (
    <li className="nav-item mb-0.5">
      <div
        className={cn(
          "flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-all duration-150",
          isActive
            ? "bg-[#eef0fd] text-[#1171ef] font-semibold"
            : "text-[#525f7f] hover:text-[#1171ef] hover:bg-[#f5f7ff] font-medium"
        )}
      >
        <Link href="/ai-bot" className="flex items-center gap-3 flex-1 min-w-0">
          <i className={cn("fas fa-brain w-5 text-center text-base shrink-0", isActive ? "text-[#1171ef]" : "text-[#8898aa]")} />
          <span className="flex-1 text-[13px]">{label}</span>
        </Link>
        {enabled !== null && (
          <button
            onClick={e => { e.preventDefault(); e.stopPropagation(); toggle(); }}
            title={enabled ? "Turn off AI Bot" : "Turn on AI Bot"}
            className={cn(
              "shrink-0 w-9 h-5 rounded-full relative transition-colors duration-300 focus:outline-none",
              enabled ? "bg-[#2dce89]" : "bg-[#f5365c]"
            )}
          >
            <span
              className={cn(
                "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all duration-300",
                enabled ? "right-0.5" : "left-0.5"
              )}
            />
          </button>
        )}
      </div>
    </li>
  );
}

function LangToggle() {
  const { lang, setLang } = useLang();

  return (
    <div className="flex items-center gap-1 bg-[#f8f9fe] border border-[#e9ecef] rounded-lg p-0.5">
      <button
        onClick={() => setLang("he")}
        title="עברית"
        className={cn(
          "flex items-center justify-center w-8 h-7 rounded-md text-base transition-all duration-150",
          lang === "he"
            ? "bg-white shadow-sm scale-105"
            : "hover:bg-white/60 opacity-60 hover:opacity-100"
        )}
      >
        🇮🇱
      </button>
      <button
        onClick={() => setLang("en")}
        title="English"
        className={cn(
          "flex items-center justify-center w-8 h-7 rounded-md text-base transition-all duration-150",
          lang === "en"
            ? "bg-white shadow-sm scale-105"
            : "hover:bg-white/60 opacity-60 hover:opacity-100"
        )}
      >
        🇺🇸
      </button>
    </div>
  );
}

export function Sidebar() {
  const { t } = useLang();

  return (
    <aside className="w-64 min-h-screen bg-white border-e border-[#e9ecef] flex flex-col shrink-0 shadow-sm">
      {/* Brand */}
      <div className="px-5 py-5 border-b border-[#e9ecef]">
        <div className="flex items-center justify-between gap-2">
          <a href="/dashboard" className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg gradient-primary flex items-center justify-center shrink-0">
              <i className="fab fa-whatsapp text-white text-lg" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-[#32325d] leading-tight">{t.brand}</p>
              <p className="text-[11px] text-[#8898aa] mt-0.5 truncate">{t.brandSub}</p>
            </div>
          </a>
          <LangToggle />
        </div>
      </div>

      {/* Main navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul className="space-y-0">
          {mainNavItems.map((item) =>
            item.href === "/ai-bot"
              ? <AiBotNavLink key={item.href} />
              : <NavLink key={item.href} item={item} />
          )}
        </ul>

        <hr className="my-4 border-[#e9ecef]" />
        <h6 className="px-4 text-[10px] font-semibold uppercase tracking-wider text-[#8898aa] mb-2">
          {t.settings}
        </h6>
        <ul className="space-y-0">
          {settingsNavItems.map((item) => (
            <NavLink key={item.href} item={item} />
          ))}
          <li className="mb-0.5">
            <button className="flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-all duration-150 text-[#525f7f] hover:text-[#f5365c] hover:bg-red-50 font-medium w-full text-start">
              <i className="fas fa-power-off w-5 text-center text-base text-[#8898aa]" />
              <span className="flex-1 text-[13px]">{t.logout}</span>
            </button>
          </li>
        </ul>
      </nav>

      <div className="px-4 py-3 border-t border-[#e9ecef]">
        <p className="text-[10px] text-[#8898aa] text-center">{t.version}</p>
      </div>
    </aside>
  );
}
